import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Wrapper, Box, Header, Title, Content, TitleWrapper } from './styles';
import { CloseIcon } from '@spa/shared/svg/close';
import useOnOutsideClickOrPressEsc from '@spa/shared/hooks/useOnOutsideClickOrPressEsc';
import useScrollLock from '@spa/shared/hooks/useScrollLock';

import { animated, useTransition } from 'react-spring';
import { upDown, TIME_UNTIL_CLOSE } from '@spa/shared/animations/upDown';

const ModalTitle = ({ icon, children }) =>
  icon ? (
    <TitleWrapper>
      {icon}
      <Title>{children}</Title>
    </TitleWrapper>
  ) : (
    <Title>{children}</Title>
  );

const Modal = ({
  title = '',
  style = {},
  children,
  withHeader = true,
  withClose = true,
  transition,
  timeUntilClose,
  onClose,
  onOutsideClickOrPressEsc,
  footer,
  icon,
  ...props
}) => {
  const ref = useRef();
  const { lockScroll, unlockScroll } = useScrollLock();
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    lockScroll();

    return () => unlockScroll();
  }, []);

  const boxTransitions = useTransition(isOpen, transition ?? upDown);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    const timeout = setTimeout(onClose, timeUntilClose ?? TIME_UNTIL_CLOSE);

    return () => clearTimeout(timeout);
  }, [isOpen]);

  const onOutsideClick = useOnOutsideClickOrPressEsc(ref, () => {
    if (onOutsideClickOrPressEsc) {
      onOutsideClickOrPressEsc();

      return;
    }

    if (!withClose) {
      return;
    }

    setIsOpen(false);
  });
  const close = withClose ? <CloseIcon className="close-icon" onClick={() => setIsOpen(false)} /> : null;

  return (
    <Wrapper style={style.wrapper} {...props}>
      {boxTransitions(
        (styles, item) =>
          item && (
            <animated.div style={styles}>
              <Box style={style.box} ref={ref} {...props}>
                {onOutsideClick}
                {withHeader ? (
                  <Header hasTitle={!!title}>
                    {title && <ModalTitle icon={icon}>{title}</ModalTitle>}
                    {close}
                  </Header>
                ) : (
                  close
                )}
                <Content style={style.content} hasTitle={!!title}>
                  {children}
                </Content>
                {footer}
              </Box>
            </animated.div>
          )
      )}
    </Wrapper>
  );
};

ModalTitle.propTypes = {
  icon: PropTypes.node,
  children: PropTypes.node
};

Modal.propTypes = {
  title: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  style: PropTypes.object,
  withHeader: PropTypes.bool,
  withClose: PropTypes.bool,
  transition: PropTypes.object,
  timeUntilClose: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onOutsideClickOrPressEsc: PropTypes.func,
  footer: PropTypes.node,
  icon: PropTypes.node,
  width: PropTypes.string,
  height: PropTypes.string,
  maxWidth: PropTypes.string,
  maxHeight: PropTypes.string
};

export default Modal;
